import * as THREE from 'three';
import type { MeshBuilder, RGB } from '../../world/view/meshBuilder';
import type { PackModel } from './modelLibrary';
import { tuneMeshyTexture } from './tune';

/**
 * Street models merged into the city chunks (docs/research/MESHY-WORLD-PLAN.md, P2a): a pack model's
 * triangles are appended to a chunk builder like any procedural prop, so a Meshy tree costs no draw call of
 * its own. The chunk materials (`props`, `foliage`) have no map of their own; their uv is free (bar the
 * cut-away's thin-iron tag, -8), so a street-model vertex carries its atlas uv shifted by STREET_UV
 * along u, and the atlas patch samples the street atlas only where u is past that mark:
 *
 *   colour   = atlas texel × vertex colour   (vertex colour = per-instance tint by the model's tint weight,
 *                                              × the baked contact shading at the foot)
 *   emissive = atlas texel × glow mask (atlas alpha) × `uStreetGlow` (the lamps' level: the phone box's
 *              panes are lit at night like the procedural `glow` boxes they replace)
 *
 * Float32 keeps about a quarter of an atlas texel at 3000, and no procedural uv comes near it (world-space
 * uvs stay inside ±1000 m).
 *
 * A vertex-baked model (scripts/model-pack.mjs bakeVertex: the trees, the dustbin) has an rgba colour (baked
 * colour + tint weight) and no texture: it is just more vertex-coloured triangles in the chunk.
 */
export const STREET_UV = 3000;

const _p = new THREE.Vector3();
const _n = new THREE.Vector3();
const _m3 = new THREE.Matrix3();

export interface StreetEmit {
  /** Per-instance tint for the model's tintable vertices (a tree's season); linear multiplier, 1 = as built. */
  tint?: RGB;
  /** Multiplier for the vertices that take no tint (a tree's trunk and limbs). */
  untinted?: RGB;
  /** Contact shading at the very foot (as PROP_FOOT / ROOT_FOOT) fading out by `footHeight` m. */
  foot?: number;
  footHeight?: number;
  /**
   * The uv a vertex-baked model's vertices take (default: the middle of the unit square). A chunk material with an
   * atlas of its own (`facade`) needs its plain white tile here, so the baked colour is not multiplied by a texture.
   */
  uv?: readonly [number, number];
}

/** Append a pack model, placed by `m`, to a chunk builder. */
export function emitStreetModel(b: MeshBuilder, model: PackModel, m: THREE.Matrix4, opts: StreetEmit = {}): void {
  const g = model.geometry;
  const pos = g.getAttribute('position');
  const nor = g.getAttribute('normal');
  const uv = g.getAttribute('uv');
  const col = g.getAttribute('color');
  _m3.getNormalMatrix(m);
  const tint = opts.tint ?? [1, 1, 1];
  const un = opts.untinted ?? [1, 1, 1];
  const foot = opts.foot ?? 1;
  const fh = opts.footHeight ?? 0.6;
  const [bu, bv] = opts.uv ?? [0.5, 0.5];
  // a vertex-baked model (rgba colour: baked colour + tint weight) samples nothing: plain uv, colour in the vertex
  const baked = !!col && col.itemSize === 4;
  const base = b.vertexCount;
  const P = b.pos;
  const N = b.nor;
  const U = b.uv;
  const C = b.col;
  for (let i = 0; i < pos.count; i++) {
    _p.fromBufferAttribute(pos, i);
    const ly = _p.y;
    _p.applyMatrix4(m);
    _n.fromBufferAttribute(nor, i).applyMatrix3(_m3).normalize();
    const w = !col ? 0 : baked ? col.getW(i) : col.getX(i);
    const k = foot === 1 ? 1 : foot + (1 - foot) * Math.min(1, Math.max(0, ly / fh));
    const r = baked ? col.getX(i) : 1;
    const g2 = baked ? col.getY(i) : 1;
    const bl = baked ? col.getZ(i) : 1;
    P.push(_p.x, _p.y, _p.z);
    N.push(_n.x, _n.y, _n.z);
    if (baked) U.push(bu, bv);
    else U.push(uv.getX(i) + STREET_UV, uv.getY(i));
    // w = 1: the tint; w = 0: the untinted multiplier; between: a blend of the two
    C.push(r * (un[0] + (tint[0] - un[0]) * w) * k, g2 * (un[1] + (tint[1] - un[1]) * w) * k, bl * (un[2] + (tint[2] - un[2]) * w) * k);
  }
  const index = g.getIndex();
  const I = b.idx;
  if (index) for (let i = 0; i < index.count; i++) I.push(base + index.getX(i));
  else for (let i = 0; i < pos.count; i++) I.push(base + i);
}

/** The atlas patch's shared uniforms (one set for every patched material). */
export interface StreetAtlasUniforms {
  uStreetAtlas: { value: THREE.Texture | null };
  uStreetGlow: { value: number };
}

export function createStreetAtlasUniforms(texture: THREE.Texture | null): StreetAtlasUniforms {
  if (texture) tuneMeshyTexture(texture, true);
  return { uStreetAtlas: { value: texture }, uStreetGlow: { value: 0 } };
}

let blank: THREE.DataTexture | null = null;

/**
 * LOW draws no street model, so the patched materials sample a 1×1 white texel instead of the atlas: on a
 * phone that starts on LOW the atlas is never uploaded (LOW keeps its texture memory).
 */
export function setStreetAtlasActive(uniforms: StreetAtlasUniforms, atlas: THREE.Texture | null, on: boolean, mats: THREE.Material[]): void {
  if (!atlas) return;
  blank ??= Object.assign(new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1), { needsUpdate: true });
  uniforms.uStreetAtlas.value = on ? atlas : blank;
  for (const m of mats) (m.userData as { textures?: THREE.Texture[] }).textures = [uniforms.uStreetAtlas.value];
}

/**
 * Let a chunk material (no map of its own) draw street models: the atlas sample where uv.x is past
 * STREET_UV, the glow mask as emissive. Chains any earlier onBeforeCompile and keeps its program key.
 */
export function patchStreetAtlas(mat: THREE.Material, uniforms: StreetAtlasUniforms): void {
  if (!uniforms.uStreetAtlas.value) return;
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey.bind(mat);
  mat.onBeforeCompile = (shader, renderer) => {
    prev.call(mat, shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nvarying vec2 vStreetUv;').replace('#include <uv_vertex>', '#include <uv_vertex>\nvStreetUv = uv;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vStreetUv;\nuniform sampler2D uStreetAtlas;\nuniform float uStreetGlow;')
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
  float bhStreetOn = step( ${(STREET_UV - 0.5).toFixed(1)}, vStreetUv.x );
  vec4 bhStreet = texture2D( uStreetAtlas, vec2( vStreetUv.x - ${STREET_UV.toFixed(1)}, vStreetUv.y ) );
  diffuseColor.rgb *= mix( vec3( 1.0 ), bhStreet.rgb, bhStreetOn );`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
  totalEmissiveRadiance += bhStreet.rgb * ( bhStreetOn * clamp( ( 1.0 - bhStreet.a ) * 2.0, 0.0, 1.0 ) * uStreetGlow );`,
      );
  };
  mat.customProgramCacheKey = () => `${prevKey()}|street`;
  // the budget read-out counts textures it finds on materials (render/budget.ts)
  (mat.userData as { textures?: THREE.Texture[] }).textures = [uniforms.uStreetAtlas.value];
  mat.needsUpdate = true;
}
